import type React from 'react';
import { type FC, useState, type ReactNode, type CSSProperties, useRef, useLayoutEffect } from 'react';
import { cn } from '../../utils/ui';
import Button from '../Button/ButtonV2';

export interface ReadMoreProps {
    className?: string;
    lineClamp?: number;
    readMoreText?: string;
    children?: ReactNode;
}

const ReadMore: FC<ReadMoreProps> = props => {
    const { className = '', lineClamp, readMoreText = 'Read More', children } = props;

    const [isExpanded, setIsExpanded] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    const canBeClamped = typeof lineClamp === 'number' && lineClamp > 0;

    useLayoutEffect(() => {
        if (!canBeClamped) {
            setShowButton(false);
            return;
        }

        const element = contentRef.current;
        if (element) {
            if (isExpanded) {
                setShowButton(true);
            } else {
                // Check if content overflows when clamped
                // Clamping styles are applied via the `style` prop on the div
                const isOverflowing = element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
                setShowButton(isOverflowing);
            }
        } else {
            setShowButton(false);
        }
    }, [children, lineClamp, isExpanded, canBeClamped]);

    function handleToggle(e: React.MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    }

    const contentStyle: CSSProperties = {};
    if (!isExpanded && canBeClamped) {
        contentStyle.display = '-webkit-box';
        contentStyle.WebkitBoxOrient = 'vertical';
        contentStyle.WebkitLineClamp = lineClamp;
        contentStyle.overflow = 'hidden';
    }

    return (
        <div className={cn('', className)}>
            <div ref={contentRef} style={contentStyle}>
                {children}
            </div>
            {showButton && canBeClamped && !isExpanded && (
                <Button variant="ghost" size="xs" onClick={handleToggle} className={'transition cursor-pointer focus:outline-none link'}>
                    {readMoreText}
                </Button>
            )}
        </div>
    );
};

export default ReadMore;
